import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
// @ts-nocheck
import app from 'flarum/forum/app';
import Modal from 'flarum/common/components/Modal';
import Button from 'flarum/common/components/Button';
import Input from 'flarum/common/components/Input';
import UserSearchResult from 'flarum/common/components/UserSearchResult';
import TradeModal from './TradeModal';
const SEARCH_DEBOUNCE_MS = 200;
const SUGGESTION_LIMIT = 6;

/**
 * Small picker modal opened from the TradesPage. Live-searches Flarum
 * users (same JSON:API filter the @-mentions composer uses) as the actor
 * types, then hands off to the full TradeModal when one is picked.
 *
 * The earlier version asked the user to type the exact username and
 * resolved it on submit — fine for power users, awful for everyone else
 * who can only remember part of a display name. Autocomplete here mirrors
 * the mentions/typeahead UX so the modal feels native.
 */
export default class StartTradeModal extends Modal {
  constructor() {
    super(...arguments);
    _defineProperty(this, "query", '');
    _defineProperty(this, "busy", false);
    _defineProperty(this, "err", '');
    _defineProperty(this, "suggestions", []);
    _defineProperty(this, "searching", false);
    _defineProperty(this, "highlight", 0);
    _defineProperty(this, "searchTimer", null);
    _defineProperty(this, "selected", null);
  }
  className() {
    return 'PointSystemStartTradeModal Modal--small';
  }
  title() {
    return app.translator.trans('ramon-point-system.forum.trades_page.start_title');
  }
  onremove(vnode) {
    var _super$onremove;
    if (this.searchTimer) {
      clearTimeout(this.searchTimer);
      this.searchTimer = null;
    }
    (_super$onremove = super.onremove) == null || _super$onremove.call(this, vnode);
  }
  content() {
    const t = k => app.translator.trans('ramon-point-system.forum.trades_page.' + k);
    const showSuggestions = this.query.trim().length >= 1 && !this.selected;
    return m("div", {
      className: "Modal-body"
    }, m("p", {
      className: "helpText"
    }, t('start_help')), m("div", {
      className: "Form-group PointSystemStartTradeModal-search"
    }, m("label", null, t('start_username_label')), m(Input, {
      className: "PointSystemStartTradeModal-searchInput",
      type: "search",
      prefixIcon: "fas fa-magnifying-glass",
      clearable: !!(this.query || this.selected),
      clearLabel: t('start_clear'),
      loading: this.searching,
      ariaLabel: t('start_username_label'),
      placeholder: t('start_username_placeholder'),
      value: this.selected ? this.formatPicked(this.selected) : this.query,
      onchange: value => this.onQueryInput(value),
      inputAttrs: {
        autocomplete: 'off',
        autofocus: true,
        role: 'combobox',
        'aria-autocomplete': 'list',
        'aria-expanded': showSuggestions,
        onkeydown: e => this.onKeyDown(e),
        onfocus: () => {
          if (this.selected) {
            var _this$selected$userna, _this$selected$userna2, _this$selected;
            // Clear selection on re-focus so the user can search again.
            this.query = String((_this$selected$userna = (_this$selected$userna2 = (_this$selected = this.selected).username) == null ? void 0 : _this$selected$userna2.call(_this$selected)) != null ? _this$selected$userna : '');
            this.selected = null;
          }
        }
      }
    }), showSuggestions && this.renderSuggestions()), this.err && m("p", {
      className: "PointSystemStartTradeModal-error"
    }, m("i", {
      className: "fas fa-exclamation-triangle"
    }), " ", this.err), m("div", {
      className: "Form-group PointSystemStartTradeModal-actions"
    }, m(Button, {
      className: "Button",
      onclick: () => this.hide()
    }, app.translator.trans('ramon-point-system.forum.trades_page.start_cancel')), m(Button, {
      className: "Button Button--primary",
      loading: this.busy,
      disabled: !this.selected || this.busy,
      onclick: () => this.submit()
    }, m("i", {
      className: "fas fa-handshake"
    }), " ", app.translator.trans('ramon-point-system.forum.trades_page.start_submit'))));
  }
  renderSuggestions() {
    const t = k => app.translator.trans('ramon-point-system.forum.trades_page.' + k);
    const hasResults = this.suggestions.length > 0;
    const showNotFound = !this.searching && !hasResults && this.query.trim().length >= 2;

    // While searching with nothing to show yet, the search bar's own inline
    // spinner is the loading affordance — don't also render an empty box.
    if (!hasResults && !showNotFound) return null;
    return m("ul", {
      className: "PointSystemStartTradeModal-suggestions"
    }, showNotFound && m("li", {
      className: "PointSystemStartTradeModal-suggestionStatus"
    }, t('start_not_found')), this.suggestions.map((u, i) => {
      var _u$username;
      return (
        // Native search-result row (avatar + query-highlighted name + badges).
        // We pass the @handle as a child so it renders to the right — useful
        // for telling apart users who share a display name. `.active` mirrors
        // the keyboard highlight; hover is handled in CSS.
        m(UserSearchResult, {
          user: u,
          query: this.query,
          className: i === this.highlight ? 'active' : '',
          onclick: () => this.pick(u)
        }, m("span", {
          className: "PointSystemStartTradeModal-handle"
        }, "@", (_u$username = u.username) == null ? void 0 : _u$username.call(u)))
      );
    }));
  }
  formatPicked(u) {
    var _u$displayName, _u$displayName2, _u$username2, _u$username3;
    const dn = String((_u$displayName = (_u$displayName2 = u.displayName) == null ? void 0 : _u$displayName2.call(u)) != null ? _u$displayName : '');
    const un = String((_u$username2 = (_u$username3 = u.username) == null ? void 0 : _u$username3.call(u)) != null ? _u$username2 : '');
    return dn && dn !== un ? "".concat(dn, " (@").concat(un, ")") : "@".concat(un);
  }
  onQueryInput(value) {
    this.query = value;
    this.selected = null;
    this.highlight = 0;
    this.err = '';
    if (this.searchTimer) clearTimeout(this.searchTimer);
    if (value.trim().length < 1) {
      this.suggestions = [];
      this.searching = false;
      m.redraw();
      return;
    }
    this.searching = true;
    m.redraw();
    this.searchTimer = setTimeout(() => this.search(value.trim()), SEARCH_DEBOUNCE_MS);
  }
  async search(q) {
    try {
      // Flarum's standard users filter — same endpoint the @-mentions
      // composer typeahead uses. Honours blocking/permissions, returns
      // already-hydrated User store records.
      const results = await app.store.find('users', {
        filter: {
          q
        },
        page: {
          limit: SUGGESTION_LIMIT
        }
      });
      const list = Array.isArray(results) ? results : [];
      // Strip self — user can't trade with themselves; surfacing self in
      // the suggestion list invites a confusing error on submit.
      const me = app.session.user;
      this.suggestions = me ? list.filter(u => {
        var _u$id, _me$id;
        return Number((_u$id = u.id) == null ? void 0 : _u$id.call(u)) !== Number((_me$id = me.id) == null ? void 0 : _me$id.call(me));
      }) : list;
      this.highlight = 0;
    } catch (e) {
      var _e$response;
      this.suggestions = [];
      const detail = (e == null || (_e$response = e.response) == null || (_e$response = _e$response.errors) == null || (_e$response = _e$response[0]) == null ? void 0 : _e$response.detail) || app.translator.trans('ramon-point-system.forum.trades_page.start_search_failed');
      app.alerts.show({
        type: 'error'
      }, detail);
    } finally {
      this.searching = false;
      m.redraw();
    }
  }
  onKeyDown(e) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (this.suggestions.length === 0) return;
      this.highlight = (this.highlight + 1) % this.suggestions.length;
      m.redraw();
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (this.suggestions.length === 0) return;
      this.highlight = (this.highlight - 1 + this.suggestions.length) % this.suggestions.length;
      m.redraw();
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      if (this.selected) {
        this.submit();
        return;
      }
      const pick = this.suggestions[this.highlight];
      if (pick) {
        this.pick(pick);
      }
      return;
    }
    if (e.key === 'Escape') {
      // Let the modal handle close — don't preventDefault.
      return;
    }
  }
  pick(u) {
    this.selected = u;
    this.suggestions = [];
    this.err = '';
    m.redraw();
  }
  async submit() {
    var _me$id2, _this$selected$id, _this$selected2;
    if (!this.selected) {
      // Backstop: if Enter is pressed before a pick is made, try resolving
      // the raw query (single result OR exact-match disambiguation).
      const q = this.query.trim();
      if (!q) return;
      this.busy = true;
      this.err = '';
      m.redraw();
      try {
        const found = await app.store.find('users', {
          filter: {
            q
          },
          page: {
            limit: 5
          }
        });
        const list = Array.isArray(found) ? found : [];
        const match = list.find(u => {
          var _u$username4, _u$username5, _u$displayName3, _u$displayName4;
          const un = String((_u$username4 = (_u$username5 = u.username) == null ? void 0 : _u$username5.call(u)) != null ? _u$username4 : '').toLowerCase();
          const dn = String((_u$displayName3 = (_u$displayName4 = u.displayName) == null ? void 0 : _u$displayName4.call(u)) != null ? _u$displayName3 : '').toLowerCase();
          const ql = q.toLowerCase();
          return un === ql || dn === ql;
        }) || list[0];
        if (!match) {
          this.err = app.translator.trans('ramon-point-system.forum.trades_page.start_not_found');
          return;
        }
        this.selected = match;
      } catch (e) {
        var _e$response2;
        this.err = (e == null || (_e$response2 = e.response) == null || (_e$response2 = _e$response2.errors) == null || (_e$response2 = _e$response2[0]) == null ? void 0 : _e$response2.detail) || 'Failed';
        return;
      } finally {
        this.busy = false;
      }
    }
    const me = app.session.user;
    if (me && Number((_me$id2 = me.id) == null ? void 0 : _me$id2.call(me)) === Number((_this$selected$id = (_this$selected2 = this.selected).id) == null ? void 0 : _this$selected$id.call(_this$selected2))) {
      this.err = app.translator.trans('ramon-point-system.forum.trade.error_cannot_trade_with_self');
      m.redraw();
      return;
    }

    // Hand off to the full trade modal.
    this.hide();
    app.modal.show(TradeModal, {
      target: this.selected
    });
  }
}
flarum.reg.add('ramon-point-system', 'forum/components/StartTradeModal', StartTradeModal);